import { useState, useEffect } from "react";
import { AlertTriangle, Cake, Calendar, CheckCircle, FileText, FolderOpen, Mail, Phone, Search, Upload, Users } from "lucide-react";
import * as XLSX from "xlsx";
import { supabase } from "./supabase";
import { fmtDate, fmtDateDisplay, parseDisplayDate, getAllocatedVac, getPolicy, sendPushToAdmin, sendPushToEmployee } from "./helpers";

export function NGtecoImport({t, lang}){
  const L=t.leave;
  const [step,setStep]=useState("upload"); // upload | preview | done
  const [rows,setRows]=useState([]);
  const [result,setResult]=useState({imported:0,skipped:0});
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [existingKeys,setExistingKeys]=useState(new Set());

  useEffect(()=>{
    // 기존 근태 기록 키 로드 (중복 방지용: name+date)
    supabase.from("leaves").select("emp_name,date,clock_in").then(({data})=>{
      if(data){
        // clock_in 있는 기록(NGteco)만 중복 체크 대상
        const keys=new Set(data.filter(r=>r.clock_in).map(r=>`${r.emp_name}__${r.date}`));
        console.log("[NGteco] Existing keys loaded:", keys.size, [...keys].slice(0,3));
        setExistingKeys(keys);
      }
    });
  },[]);

  async function handleFile(file){
    setError("");
    setRows([]);
    setStep("upload");
    if(!file) return;
    try{
      const ab=await file.arrayBuffer();
      const wb=XLSX.read(ab,{type:"array"});
      const ws=wb.Sheets[wb.SheetNames[0]];

      // NGteco Excel: 1행=제목("Timecard Report"), 2행=헤더
      // raw 배열로 읽어서 "First Name" 헤더가 있는 행을 찾음
      const allRows=XLSX.utils.sheet_to_json(ws,{header:1,defval:""});
      let headerIdx=-1;
      for(let i=0;i<allRows.length;i++){
        const r=allRows[i].map(v=>String(v).trim().toLowerCase());
        if(r.includes("first name")||r.includes("name")){headerIdx=i;break;}
      }
      if(headerIdx===-1){setError(L.importError);return;}

      const headers=allRows[headerIdx].map(v=>String(v).trim());
      const dataRows=allRows.slice(headerIdx+1);

      function col(row,names){
        for(const n of names){
          const idx=headers.findIndex(h=>h.toLowerCase()===n.toLowerCase());
          if(idx!==-1&&row[idx]!==undefined&&row[idx]!=="") return String(row[idx]).trim();
        }
        return "";
      }

      const parsed=[];
      for(const row of dataRows){
        const firstName=col(row,["First Name"]);
        const lastName=col(row,["Last Name"]);
        const name=col(row,["Name"])||(firstName?`${firstName} ${lastName}`.trim():"");
        const dateRaw=col(row,["Date"]);
        if(!name||!dateRaw) continue;

        // date 포맷 통일 (MM/DD/YYYY → YYYY-MM-DD)
        let date=dateRaw;
        if(dateRaw.includes("/")){
          const [m,d,y]=dateRaw.split("/");
          date=`${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
        } else if(!isNaN(Number(dateRaw))){
          const d=new Date(Math.round((Number(dateRaw)-25569)*86400*1000));
          date=d.toISOString().slice(0,10);
        }

        parsed.push({
          name,
          date,
          timesheet:col(row,["Timesheet"]),
          clock_in:col(row,["Clock In"]),
          clock_out:col(row,["Clock Out"]),
          clock_time:col(row,["Clock Time(h)","Clock Time"]),
          break_time:col(row,["Total Break Time(h)","Total Break Time"]),
          work_time:col(row,["Total Work Time(h)","Total Work Time"]),
          total_time:col(row,["Total Time(h)","Total Time"]),
          ot_time:col(row,["Total Overtime Time(h)","Total Overtime Time"]),
          abnormal_situation:col(row,["Abnormal Situation"]),
        });
      }

      if(parsed.length===0){setError(L.importError);return;}
      setRows(parsed);
      setStep("preview");
    }catch(e){setError(L.importError+": "+e.message);}
  }

  async function runImport(){
    setLoading(true);
    let imported=0,skipped=0;
    for(const row of rows){
      const key=`${row.name}__${row.date}`;
      if(existingKeys.has(key)){skipped++;continue;}
      const payload={
        emp_name:row.name, date:row.date,
        type:"특이사항",
        note:`[NGteco] In:${row.clock_in||"-"} Out:${row.clock_out||"-"} Work:${row.work_time||"-"}h OT:${row.ot_time||"-"}h${row.abnormal_situation?" [!] "+row.abnormal_situation:""}`,
        clock_in:row.clock_in||null, clock_out:row.clock_out||null,
        clock_time:row.clock_time||null, break_time:row.break_time||null,
        work_time:row.work_time||null, total_time:row.total_time||null,
        ot_time:row.ot_time||null, abnormal_situation:row.abnormal_situation||null,
        timesheet:row.timesheet||null,
      };
      const{error}=await supabase.from("leaves").insert([payload]);
      if(!error){imported++;existingKeys.add(key);}
      else{ console.error("[NGteco] Insert error:",error.message,"code:",error.code,"payload:",JSON.stringify(payload)); skipped++; }
    }
    setResult({imported,skipped});
    setStep("done");
    setLoading(false);
  }

  const previewCols=[
    {k:"name",l:L.colName},{k:"date",l:L.colDate},{k:"timesheet",l:L.colTimesheet},
    {k:"clock_in",l:L.colIn},{k:"clock_out",l:L.colOut},
    {k:"clock_time",l:L.colClock},{k:"break_time",l:L.colBreak},
    {k:"work_time",l:L.colWork},{k:"total_time",l:L.colTotal},
    {k:"ot_time",l:L.colOT},{k:"abnormal_situation",l:L.colAbnormal},
  ];

  return(
    <>
      <div className="ph">
        <div className="pt"><Upload size={20}/> {L.importTitle}</div>
        <div className="ps">{L.importSub}</div>
      </div>

      {step==="upload"&&(
        <div className="card" style={{textAlign:"center",padding:"40px 20px"}}>
          <div style={{fontSize:48,marginBottom:16,display:"flex",justifyContent:"center"}}><Upload size={48} color="var(--tx3)"/></div>
          <div style={{fontSize:15,fontWeight:700,marginBottom:8,color:"var(--tx)"}}>Timecard Report</div>
          <div style={{fontSize:12,color:"var(--tx3)",marginBottom:24,lineHeight:1.8}}>
            {L.importCols}<br/>
            <span style={{color:"var(--ye)"}}>※ {L.importDupKey}</span>
          </div>
          <label style={{display:"inline-block",background:"var(--ac)",color:"#fff",padding:"12px 28px",borderRadius:10,cursor:"pointer",fontWeight:700,fontSize:14}}>
            <FolderOpen size={16} style={{marginRight:6}}/>{L.importBtn}
            <input type="file" accept=".xlsx,.xls" style={{display:"none"}}
              onChange={e=>e.target.files[0]&&handleFile(e.target.files[0])}/>
          </label>
          {error&&<div style={{marginTop:16,color:"var(--re)",fontSize:13}}><AlertTriangle size={14} style={{marginRight:4}}/>{error}</div>}
        </div>
      )}

      {step==="preview"&&(
        <div className="card">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
            <div>
              <span style={{fontWeight:700,fontSize:15}}>{L.importPreview}</span>
              <span style={{marginLeft:10,fontSize:13,color:"var(--tx3)"}}>{rows.length}{lang==="kr"?"건":lang==="es"?"filas":"rows"}</span>
              <span style={{marginLeft:8,fontSize:12,color:"var(--ye)"}}>({lang==="kr"?"중복 스킵 예정":lang==="es"?"se omitirán duplicados":"will skip duplicates"})</span>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn bsec" onClick={()=>setStep("upload")}>{L.cancel||(lang==="kr"?"다시 선택":lang==="es"?"Elegir de nuevo":"Choose again")}</button>
              <button className="btn bp" onClick={runImport} disabled={loading}>
                {loading?"...":`${L.importConfirm} (${rows.filter(r=>!existingKeys.has(`${r.name}__${r.date}`)).length}${lang==="kr"?"건":""})`}
              </button>
            </div>
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{fontSize:11,minWidth:900}}>
              <thead>
                <tr>{previewCols.map(c=>(
                  <th key={c.k} style={{background:"var(--sf2)",color:"var(--tx)",padding:"6px 8px",fontWeight:700,whiteSpace:"nowrap",borderBottom:"2px solid var(--bd)"}}>
                    {c.l}
                  </th>
                ))}</tr>
              </thead>
              <tbody>{rows.map((row,i)=>{
                const isDup=existingKeys.has(`${row.name}__${row.date}`);
                return(
                  <tr key={i} style={{background:isDup?"rgba(220,38,38,.06)":i%2===0?"":"var(--sf2)",opacity:isDup?.5:1}}>
                    {previewCols.map(col=>(
                      <td key={col.k} style={{padding:"5px 8px",borderBottom:"1px solid var(--bd)",whiteSpace:"nowrap",
                        color:col.k==="abnormal_situation"&&row[col.k]?"var(--re)":
                              col.k==="ot_time"&&row[col.k]?"var(--or)":"var(--tx)"}}>
                        {isDup&&col.k==="name"?<span style={{fontSize:10,color:"var(--re)",marginRight:4}}>⊘</span>:null}
                        {row[col.k]||"—"}
                      </td>
                    ))}
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </div>
      )}

      {step==="done"&&(
        <div className="card" style={{textAlign:"center",padding:"40px 20px"}}>
          <div style={{fontSize:48,marginBottom:16,display:"flex",justifyContent:"center"}}><CheckCircle size={48} color="var(--gr)"/></div>
          <div style={{fontSize:18,fontWeight:700,color:"var(--gr)",marginBottom:8}}>
            {result.imported} {L.importSuccess}
          </div>
          {result.skipped>0&&(
            <div style={{fontSize:14,color:"var(--tx3)",marginBottom:16}}>
              {result.skipped} {L.importSkip}
            </div>
          )}
          <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:16}}>
            <button className="btn bp" onClick={()=>{setStep("upload");setRows([]);}}>
              {lang==="kr"?"추가 파일 가져오기":lang==="es"?"Importar otro archivo":"Import Another File"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}


export function EmployeeManager({t, lang, refreshKey}){
  const [employees,setEmployees]=useState([]);
  const [tab,setTab]=useState("list");
  const [editId,setEditId]=useState(null);
  const today=fmtDate(new Date());
  const todayDisplay=fmtDateDisplay(today);
  const def={name:"",position:"",dept:"",join_date:todayDisplay,birthday:"",phone:"",email:"",status:"Active",vac_adjust:"0",note:""};
  const [form,setForm]=useState(def);
  const [yearLeaves,setYearLeaves]=useState([]);
  const [search,setSearch]=useState("");
  const [showSearch,setShowSearch]=useState(false);
  const [policy,setPolicy]=useState(null);
  useEffect(()=>{ getPolicy().then(setPolicy); },[]);
  const [expandedEmps,setExpandedEmps]=useState(new Set());
  const [returnTab,setReturnTab]=useState("list");
  const terminatedCount=employees.filter(e=>e.status==="Inactive").length;
  const activeCount=employees.filter(e=>e.status!=="Inactive").length;
  const [isMobile,setIsMobile]=useState(window.innerWidth<768);
  useEffect(()=>{
    const handler=()=>setIsMobile(window.innerWidth<768);
    window.addEventListener("resize",handler);
    return()=>window.removeEventListener("resize",handler);
  },[]);

  useEffect(()=>{
    fetchEmps();
    const y=new Date().getFullYear();
    supabase.from("leaves").select("*").gte("date",`${y}-01-01`).lte("date",`${y}-12-31`).then(({data})=>{ if(data) setYearLeaves(data); });
  },[refreshKey]);

  async function fetchEmps(){ const{data}=await supabase.from("employees").select("*").order("join_date",{ascending:true,nullsFirst:false}); if(data) setEmployees(data); }
  function sf(k,v){ setForm(p=>({...p,[k]:v})); }
  function startEdit(e){ setForm({name:e.name,position:e.position||"",dept:e.dept||"",join_date:fmtDateDisplay(e.join_date)||"",birthday:fmtDateDisplay(e.birthday)||"",phone:e.phone||"",email:e.email||"",status:e.status||"Active",vac_adjust:String(e.vac_adjust||0),note:e.note||""}); setEditId(e.id); setReturnTab(e.status==="Inactive"?"terminated":"list"); setTab("add"); }
  async function save(){
    if(!form.name) return alert(t.emp?.nameRequired||(lang==="kr"?"이름을 입력해주세요":lang==="es"?"Ingresa el nombre":"Please enter a name"));
    const payload={name:form.name,position:form.position,dept:form.dept,join_date:parseDisplayDate(form.join_date),birthday:form.birthday?parseDisplayDate(form.birthday):null,phone:form.phone,email:form.email,status:form.status,vac_adjust:parseFloat(form.vac_adjust)||0,note:form.note};
    if(editId) await supabase.from("employees").update(payload).eq("id",editId);
    else await supabase.from("employees").insert([payload]);
    setForm(def); setEditId(null); fetchEmps(); setTab(payload.status==="Inactive"?"terminated":"list");
  }
  async function del(id){ if(window.confirm(t.leave?.confirmDel||(lang==="kr"?"삭제하시겠습니까?":lang==="es"?"¿Eliminar?":"Delete?"))){ await supabase.from("employees").delete().eq("id",id); fetchEmps(); } }
  async function deactivate(emp){
    const msg=lang==="kr"
      ?`${emp.name}님을 퇴사 처리하시겠습니까?\n- 재직 상태가 '퇴사'로 변경됩니다\n- 앱 로그인/접근이 차단됩니다\n(퇴사 사유, 최종근무일 등 상세 기록은 '인사 분리' 메뉴에서 별도로 작성해주세요)`
      :lang==="es"
      ?`¿Procesar la baja de ${emp.name}?\n- El estado cambiará a 'Inactivo'\n- Se revocará el acceso a la app\n(Completa el motivo, el último día, etc. por separado en el menú de Registros de RR. HH.)`
      :`Process termination for ${emp.name}?\n- Status will be set to 'Inactive'\n- App login/access will be revoked\n(Please fill in reason, last day, etc. separately under the Separation menu)`;
    if(!window.confirm(msg)) return;
    await supabase.from("employees").update({status:"Inactive"}).eq("id",emp.id);
    const{error:profErr}=await supabase.from("profiles").update({approved:false}).eq("emp_name",emp.name);
    if(profErr) console.warn("[deactivate] profiles update failed (계정이 없을 수 있음):",profErr.message);
    try{
      await sendPushToAdmin(supabase,"퇴사 처리 완료",`${emp.name}님이 퇴사 처리되었습니다.`,"Termination Processed",`${emp.name} has been marked as terminated.`,"/emp",{title:"Baja procesada",body:`${emp.name} fue dado de baja.`});
    }catch(e){ console.warn("push failed",e); }
    fetchEmps();
  }
  async function reinstate(emp){
    const msg=lang==="kr"
      ?`${emp.name}님을 복직 처리하시겠습니까?\n- 재직 상태가 '재직'으로 변경됩니다\n- 앱 로그인/접근 권한이 복구됩니다`
      :lang==="es"
      ?`¿Reincorporar a ${emp.name}?\n- El estado cambiará a 'Activo'\n- Se restaurará el acceso a la app`
      :`Reinstate ${emp.name}?\n- Status will be set to 'Active'\n- App login/access will be restored`;
    if(!window.confirm(msg)) return;
    await supabase.from("employees").update({status:"Active"}).eq("id",emp.id);
    const{error:profErr}=await supabase.from("profiles").update({approved:true}).eq("emp_name",emp.name);
    if(profErr) console.warn("[reinstate] profiles update failed (계정이 없을 수 있음):",profErr.message);
    try{
      await sendPushToEmployee(supabase,emp.name,"계정 복구 완료","관리자가 계정을 복구했습니다. 로그인해서 이용해보세요.","Account Reinstated","Your account has been reinstated by the admin. You can now log in.","/",{title:"Cuenta restablecida",body:"El administrador restableció tu cuenta. Ya puedes iniciar sesión."});
    }catch(e){ console.warn("push failed",e); }
    fetchEmps();
  }

  function getUsedVac(name){
    return yearLeaves.filter(l=>l.emp_name===name&&(l.type==="연차"||l.type==="Annual Leave")&&l.status==="approved").reduce((s,l)=>s+parseFloat(l.days||1),0);
  }

  return(
    <>
      <div className="ph"><div className="pt"><Users size={20}/> {lang==="kr"?"직원 관리":lang==="es"?"Gestión de empleados":"Employee Management"}</div><div className="ps">{lang==="kr"?"직원 정보를 등록하고 관리합니다.":lang==="es"?"Administra la información de los empleados":"Manage employee information"}</div></div>
      <div className="tabs">
        <button className={`tab ${tab==="list"?"active":""}`} onClick={()=>{setTab("list");setEditId(null);setForm(def);}}>{lang==="kr"?"재직 직원":lang==="es"?"Activos":"Active"}{activeCount>0&&<span style={{marginLeft:6,background:"var(--gr)",color:"#fff",borderRadius:"50%",fontSize:10,fontWeight:700,padding:"1px 5px"}}>{activeCount}</span>}</button>
        <button className={`tab ${tab==="terminated"?"active":""}`} onClick={()=>{setTab("terminated");setEditId(null);setForm(def);}}>{lang==="kr"?"퇴사 직원":lang==="es"?"Dados de baja":"Terminated"}{terminatedCount>0&&<span style={{marginLeft:6,background:"var(--tx3)",color:"#fff",borderRadius:"50%",fontSize:10,fontWeight:700,padding:"1px 5px"}}>{terminatedCount}</span>}</button>
        <button className={`tab ${tab==="add"?"active":""}`} onClick={()=>{setTab("add");setEditId(null);setForm(def);setReturnTab("list");}}>+ {lang==="kr"?"직원 추가":lang==="es"?"Agregar empleado":"Add Employee"}</button>
      </div>
      {tab==="add"&&(
        <div className="card">
          <div className="ct">{editId?(lang==="kr"?"직원 수정":lang==="es"?"Editar empleado":"Edit Employee"):(lang==="kr"?"직원 추가":lang==="es"?"Agregar empleado":"Add Employee")}</div>
          <div className="fg">
            <div className="fl"><label>{lang==="kr"?"이름 *":lang==="es"?"Nombre *":"Name *"}</label><input value={form.name} onChange={e=>sf("name",e.target.value)}/></div>
            <div className="fl"><label>{lang==="kr"?"직책":lang==="es"?"Puesto":"Position"}</label><input value={form.position} onChange={e=>sf("position",e.target.value)}/></div>
            <div className="fl"><label>{lang==="kr"?"부서":lang==="es"?"Departamento":"Department"}</label><input value={form.dept} onChange={e=>sf("dept",e.target.value)}/></div>
            <div className="fl"><label>{lang==="kr"?"입사일":lang==="es"?"Fecha de ingreso":"Join Date"}</label><input type="text" value={form.join_date} onChange={e=>sf("join_date",e.target.value)} placeholder="MM/DD/YYYY"/></div>
            <div className="fl"><label>{lang==="kr"?"생일":lang==="es"?"Cumpleaños":"Birthday"}</label><input type="text" value={form.birthday||""} onChange={e=>{
              const digits=e.target.value.replace(/\D/g,"").slice(0,8);
              const formatted=digits.length<=2?digits:digits.length<=4?`${digits.slice(0,2)}/${digits.slice(2)}`:`${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
              sf("birthday",formatted);
            }} placeholder="MM/DD/YYYY"/></div>
            <div className="fl"><label>{lang==="kr"?"연락처":lang==="es"?"Teléfono":"Phone"}</label><input value={form.phone} onChange={e=>{
              const digits=e.target.value.replace(/\D/g,"").slice(0,10);
              const formatted=digits.length<=3?digits:digits.length<=6?`${digits.slice(0,3)}-${digits.slice(3)}`:`${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
              sf("phone",formatted);
            }} placeholder={lang==="es"?"p. ej. 201-123-4567":"e.g. 201-123-4567"}/></div>
            <div className="fl"><label>{lang==="es"?"Correo electrónico":"Email"}</label><input value={form.email} onChange={e=>sf("email",e.target.value)}/></div>
            <div className="fl"><label>{lang==="kr"?"상태":lang==="es"?"Estado":"Status"}</label>
              <select value={form.status} onChange={e=>sf("status",e.target.value)}>
                <option value="Active">{lang==="kr"?"재직":lang==="es"?"Activo":"Active"}</option><option value="Inactive">{lang==="kr"?"퇴사":lang==="es"?"Inactivo":"Inactive"}</option>
              </select>
            </div>
            <div className="fl"><label>{lang==="kr"?"연차 조정 (일)":lang==="es"?"Ajuste de vacaciones (días)":"Vac Adjust (days)"}</label><input type="number" step="0.5" value={form.vac_adjust} onChange={e=>sf("vac_adjust",e.target.value)}/></div>
            <div className="fl ff"><label>{lang==="kr"?"메모":lang==="es"?"Nota":"Note"}</label><input value={form.note} onChange={e=>sf("note",e.target.value)}/></div>
          </div>
          <div style={{marginTop:14,display:"flex",gap:8}}>
            <button className="btn bp" onClick={save}>{lang==="kr"?"저장":lang==="es"?"Guardar":"Save"}</button>
            <button className="btn bsec" onClick={()=>{setTab(returnTab);setEditId(null);setForm(def);}}>{lang==="kr"?"취소":lang==="es"?"Cancelar":"Cancel"}</button>
          </div>
        </div>
      )}
      {(tab==="list"||tab==="terminated")&&(()=>{
        const scopeEmployees=employees.filter(e=>tab==="list"?e.status!=="Inactive":e.status==="Inactive");
        const filtered=scopeEmployees.filter(emp=>{
          const q=search.toLowerCase();
          return !q||emp.name?.toLowerCase().includes(q)||emp.position?.toLowerCase().includes(q)||emp.dept?.toLowerCase().includes(q);
        });
        return(
        <>
        <div style={{marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
          <button onClick={()=>{setShowSearch(s=>!s);if(showSearch)setSearch("");}} style={{background:"var(--sf2)",border:"1px solid var(--bd)",borderRadius:8,padding:"6px 10px",cursor:"pointer",fontSize:16,color:"var(--tx2)",lineHeight:1}}><Search size={15}/></button>
          {showSearch&&<input autoFocus value={search} onChange={e=>setSearch(e.target.value)}
            placeholder={lang==="kr"?"이름, 직책, 부서 검색...":lang==="es"?"Buscar por nombre, puesto, departamento...":"Search by name, position, dept..."}
            style={{flex:1,padding:"6px 14px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:13}}
          />}
        </div>
        {filtered.length===0?<div className="card"><div className="empty">{search?(lang==="kr"?"검색 결과가 없습니다":lang==="es"?"No se encontraron resultados":"No results found"):(tab==="terminated"?(lang==="kr"?"퇴사 처리된 직원이 없습니다":lang==="es"?"No hay empleados dados de baja":"No terminated employees"):(lang==="kr"?"재직중인 직원이 없습니다":lang==="es"?"No hay empleados activos":"No active employees"))}</div></div>:
        filtered.map(emp=>{
          const alloc=getAllocatedVac(emp,policy?.vacation_tiers);
          const used=getUsedVac(emp.name);
          const remain=alloc-used;
          const isOpen=expandedEmps.has(emp.id);
          return(
            <div className="card" key={emp.id} style={tab==="terminated"?{opacity:.75}:undefined}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}} onClick={()=>setExpandedEmps(prev=>{const n=new Set(prev);n.has(emp.id)?n.delete(emp.id):n.add(emp.id);return n;})}>
                <div>
                  {isMobile?(
                    <>
                      <div style={{fontWeight:700,fontSize:15}}>{emp.name}</div>
                      <div style={{marginTop:3,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                        {(emp.position||emp.dept)&&<span style={{fontSize:12,color:"var(--tx3)"}}>{emp.position||""}{emp.dept?` · ${emp.dept}`:""}</span>}
                        <span style={{padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:700,background:emp.status==="Active"?"rgba(5,150,105,.1)":"rgba(107,114,128,.1)",color:emp.status==="Active"?"var(--gr)":"var(--tx3)"}}>{lang==="kr"?(emp.status==="Active"?"재직":"퇴사"):lang==="es"?(emp.status==="Active"?"Activo":"Inactivo"):emp.status}</span>
                      </div>
                    </>
                  ):(
                    <div style={{fontWeight:700,fontSize:15}}>
                      {emp.name}
                      <span style={{marginLeft:8,fontSize:12,color:"var(--tx3)",fontWeight:400}}>{emp.position||""}{emp.dept?` · ${emp.dept}`:""}</span>
                      <span style={{marginLeft:8,padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:700,background:emp.status==="Active"?"rgba(5,150,105,.1)":"rgba(107,114,128,.1)",color:emp.status==="Active"?"var(--gr)":"var(--tx3)"}}>{lang==="kr"?(emp.status==="Active"?"재직":"퇴사"):lang==="es"?(emp.status==="Active"?"Activo":"Inactivo"):emp.status}</span>
                    </div>
                  )}
                </div>
                <span style={{fontSize:12,color:"var(--tx3)"}}>{isOpen?"▲":"▼"}</span>
              </div>
              {isOpen&&(
                <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--bd)"}}>
                  <div style={{fontSize:12,color:"var(--tx3)",marginBottom:6}}>
                    <Calendar size={11} style={{marginRight:3}}/>{lang==="kr"?"입사":lang==="es"?"Ingreso":"Joined"}: {fmtDateDisplay(emp.join_date)}
                    {emp.birthday&&<span> · <Cake size={11} style={{marginRight:3}}/>{fmtDateDisplay(emp.birthday)}</span>}
                  </div>
                  <div style={{fontSize:12,color:"var(--tx3)",marginBottom:6}}>
                    {emp.phone&&<span><Phone size={11} style={{marginRight:3}}/>{emp.phone}</span>}
                    {emp.email&&<span style={{marginLeft:emp.phone?12:0}}><Mail size={11} style={{marginRight:3}}/>{emp.email}</span>}
                  </div>
                  <div style={{marginTop:8}}>
                    <div style={{fontSize:12,marginBottom:4}}>
                      <span style={{color:"var(--ac)",fontWeight:600}}>{lang==="kr"?"잔여연차":lang==="es"?"Restantes":"Remaining"}: {remain.toFixed(1)}{lang==="kr"?"일":lang==="es"?"d":"d"}</span>
                      <span style={{color:"var(--tx3)",marginLeft:8}}>{lang==="kr"?"(총":lang==="es"?"(Total":"(Total"} {alloc}{lang==="kr"?"일 - 사용":lang==="es"?"d - usados":"d - used"} {used}{lang==="kr"?"일)":lang==="es"?"d)":"d)"}</span>
                    </div>
                    {emp.note&&<div style={{fontSize:12,color:"var(--tx3)",marginBottom:4}}><FileText size={12} style={{marginRight:4}}/>{emp.note}</div>}
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
                      {tab==="list"
                        ?<button className="btn bs" style={{background:"rgba(217,119,6,.1)",color:"var(--ye)",border:"1px solid rgba(217,119,6,.3)"}} onClick={e=>{e.stopPropagation();deactivate(emp);}}>{lang==="kr"?"퇴사 처리":lang==="es"?"Procesar baja":"Process Termination"}</button>
                        :<button className="btn bs" style={{background:"rgba(5,150,105,.1)",color:"var(--gr)",border:"1px solid rgba(5,150,105,.3)"}} onClick={e=>{e.stopPropagation();reinstate(emp);}}>{lang==="kr"?"복직 처리":lang==="es"?"Reincorporar":"Reinstate"}</button>
                      }
                      <button className="btn bs" style={{background:"var(--sf2)",color:"var(--tx2)",border:"1px solid var(--bd)"}} onClick={e=>{e.stopPropagation();startEdit(emp);}}>{lang==="kr"?"수정":lang==="es"?"Editar":"Edit"}</button>
                      <button className="btn bs bd2" onClick={e=>{e.stopPropagation();del(emp.id);}}>{lang==="kr"?"삭제":lang==="es"?"Eliminar":"Delete"}</button>
                    </div>
                  </div>
                </div>

              )}
            </div>
          );
        })
        }</>
        );
      })()}
    </>
  );
}
